import { ErrorMessage, Form, Formik } from 'formik';
import moment from 'moment';
import { boolean, date, object } from 'yup';

import { Button } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import { Calendar } from '@/molecules';

import { isDayBlocked } from '@/utils';

import type { FunctionComponent } from 'react';
import type { ExactasForm, TypeCalendarTabsProps } from './types';

const exactDate = ({ minDays }: { minDays: number }) =>
  object()
    .shape({
      validRange: boolean().required(
        `La estancia mínima es de ${minDays} días`
      ),
      startDate: date().required('La fecha de inicio es obligatoria'),
      endDate: date().required('La fecha de fin es obligatoria'),
    })
    .test(
      'validRange',
      `La estancia mínima es de ${minDays} días`,
      (value, { createError }) => {
        if (
          moment(value.endDate).diff(moment(value.startDate), 'days') <
          minDays - 1
        ) {
          return createError({
            path: 'validRange',
            message: `La estancia mínima es de ${minDays} días`,
          });
        }

        return true;
      }
    );

/**
 * CalendarTabs
 */
export const CalendarTabs: FunctionComponent<TypeCalendarTabsProps> = ({
  message = '',
  minDays,
  onSubmit,
  availableDates,
}: TypeCalendarTabsProps) => (
  <Formik
    initialValues={
      {
        startDate: moment(),
        endDate: moment(),
        validRange: false,
      } as ExactasForm
    }
    validationSchema={exactDate({ minDays })}
    onSubmit={onSubmit}
  >
    {({ setFieldValue, values, isSubmitting }) => (
      <Form noValidate className="flex flex-col items-center">
        <>
          <Calendar
            numberOfMonths={2}
            variant="booking"
            type="range"
            className="hidden px-2 md:flex"
            onChange={({ startDate, endDate }) => {
              setFieldValue('startDate', startDate);
              setFieldValue('endDate', endDate);
            }}
            {...(availableDates && {
              isDayBlocked: (day) => isDayBlocked(day, availableDates),
            })}
            startDate={values.startDate}
            endDate={values.endDate}
          />

          <Calendar
            numberOfMonths={1}
            variant="booking"
            type="range"
            className="flex px-2 md:hidden"
            onChange={({ startDate, endDate }) => {
              setFieldValue('startDate', startDate);
              setFieldValue('endDate', endDate);
            }}
            {...(availableDates && {
              isDayBlocked: (day) => isDayBlocked(day, availableDates),
            })}
            startDate={values.startDate}
            endDate={values.endDate}
          />

          <div className="mx-auto mt-2 flex w-full flex-col gap-2 px-2 text-center">
            <ErrorMessage name="startDate">
              {(msg) => (
                <p className="text-body text-body--m text-status-error">
                  {msg}
                </p>
              )}
            </ErrorMessage>

            <ErrorMessage name="endDate">
              {(msg) => (
                <p className="text-body text-body--m text-status-error">
                  {msg}
                </p>
              )}
            </ErrorMessage>

            <ErrorMessage name="validRange">
              {(msg) => (
                <p className="text-body text-body--m text-status-error">
                  {msg}
                </p>
              )}
            </ErrorMessage>
          </div>

          <hr className="mt-2 h-px w-full text-neutrals-100" />

          <div className="flex w-full flex-col items-center justify-between gap-4 pt-4 md:flex-row">
            {message && (
              <p className="text-body text-body--m font-bold">{message}</p>
            )}

            <div className="flex gap-4 md:ml-auto">
              <Button
                type="button"
                outline
                size={ButtonSize.SMALL}
                variant={ButtonVariant.PRIMARY}
                className="w-full"
                onClick={() => {
                  setFieldValue('startDate', null);
                  setFieldValue('endDate', null);
                }}
              >
                Limpiar
              </Button>

              <Button
                type="submit"
                size={ButtonSize.SMALL}
                variant={ButtonVariant.PRIMARY}
                className="w-full"
                disabled={isSubmitting}
              >
                Aceptar
              </Button>
            </div>
          </div>
        </>
      </Form>
    )}
  </Formik>
);

CalendarTabs.displayName = 'CalendarTabs';
